import { Link } from 'react-router';
import { CheckCircle2, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCurrentProgressReport } from '@/services/progress-reports';

/**
 * Whether this week's progress report is in, at the top of the dashboard.
 *
 * The prompt moved out of the inbox and into the product (#244). Until now the
 * only route to the tracker was two cards near the bottom of this page, below
 * the fold, and the compensation for that was a reminder email every seven
 * days — which is what #288 was about.
 *
 * Deliberately a sentence rather than a badge. The audience is 50+ and this has
 * to say what it is, why it matters and what to do, without anything to decode.
 * It also says so when the report IS in: a prompt that simply vanishes leaves
 * the client wondering whether it landed.
 *
 * Colour is never the only channel. Each state has its own words and its own
 * icon, so the fills below are reinforcement rather than information.
 *
 * The two fills come from the P6 brand tokens and are used as fills only, which
 * is what those tokens are for. Both carry --brand-fill-ink, and both ratios
 * were measured with the WCAG 2.1 formula rather than eyeballed:
 *
 *   #1a1a1a on --brand-fill-yellow #f4c046 — 10.34:1
 *   #1a1a1a on --brand-fill-green  #5baf7b —  6.52:1
 *   #ffffff on #1a1a1a (the button)        — 17.40:1
 *
 * The banner's own edge against the white page is 1.68:1 and 2.67:1, which is
 * a visible tint at this size and carries nothing — the words carry the state —
 * so 1.4.11 does not apply to it.
 */
export function WeeklyReportPrompt() {
    const { data, isPending, isError } = useCurrentProgressReport();

    // Nothing at all while it loads. A skeleton at the top of the dashboard
    // would shift everything below it the moment the answer arrives.
    if (isPending) return null;

    // Not silence. A client whose status could not be read still needs the way
    // in, and the tracker itself will tell them where they stand.
    if (isError || !data) {
        return (
            <Card>
                <div className="flex-1">
                    <h2 className="text-lg font-bold text-gray-900">Weekly progress report</h2>
                    <p className="mt-1 text-gray-700">
                        We could not check your report just now. You can still open the tracker to
                        see where you are.
                    </p>
                </div>
                <Link to="/progress" className="shrink-0">
                    <Button variant="outline" className="min-h-11 px-6">
                        Open my progress
                    </Button>
                </Link>
            </Card>
        );
    }

    const { submittedThisWeek, hasEverSubmitted, daysSinceLastReport } = data.status;

    if (submittedThisWeek) {
        return (
            <Card className="bg-brand-fill-green text-brand-fill-ink">
                <CheckCircle2 className="h-6 w-6 shrink-0" aria-hidden="true" />
                <div className="flex-1">
                    <h2 className="text-lg font-bold">This week’s report is in</h2>
                    <p className="mt-1">
                        Thank you — your coach has your answers. You can still change them if
                        something was not quite right.
                    </p>
                </div>
                <Link to="/progress-report" className="shrink-0">
                    <Button className="min-h-11 bg-brand-fill-ink px-6 text-white hover:bg-black">
                        Update my report
                    </Button>
                </Link>
            </Card>
        );
    }

    return (
        <Card className="bg-brand-fill-yellow text-brand-fill-ink">
            <ClipboardList className="h-6 w-6 shrink-0" aria-hidden="true" />
            <div className="flex-1">
                <h2 className="text-lg font-bold">Your weekly progress report is due</h2>
                <p className="mt-1">
                    {/* Nothing here mentions a programme. Reports track the
                        coaching relationship, so a client with no programme
                        attached gets exactly the same prompt. */}
                    {hasEverSubmitted
                        ? `It has been ${daysSinceLastReport} ${
                              daysSinceLastReport === 1 ? 'day' : 'days'
                          } since your last one. It takes about five minutes, and it is what your coach reads before your next session.`
                        : 'This is your first one. It takes about five minutes, and it is what your coach reads before your next session.'}
                </p>
            </div>
            <Link to="/progress-report" className="shrink-0">
                <Button className="min-h-11 bg-brand-fill-ink px-6 text-white hover:bg-black">
                    {hasEverSubmitted ? 'Send this week’s report' : 'Send my first report'}
                </Button>
            </Link>
        </Card>
    );
}

/**
 * The shell. Stacks on a phone and sits on one line from `sm` up, so the button
 * is never squeezed next to the text at 360px.
 */
function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
    return (
        <section
            className={`mb-8 flex flex-col items-start gap-4 rounded-2xl p-5 shadow-sm sm:flex-row sm:items-center sm:p-6 ${
                className || 'border border-brand-surface-edge bg-brand-surface'
            }`}
        >
            {children}
        </section>
    );
}
